import type { BinaryImage } from './threshold.js';
import type { DetectedLine, Detection, Detector, DetectorContext } from './detector.js';
import { assembleRectangles } from './rectangles.js';
import { detectCircles } from './circles.js';

/**
 * A standard Hough transform for lines, and nothing else yet.
 *
 * This is the detector that ships, so it is deliberately the simple one: no
 * dependency, runs in the browser, and its failure modes are the textbook ones
 * rather than a library's. Circles are not attempted here — the OpenCV
 * comparison in `kernel/verify/detect_opencv.py` is what decides whether a Hough
 * circle transform is worth carrying, and ADR 0009 records that decision. Until
 * then this detector returns lines and says so.
 *
 * Confidence is the vote count normalised by the longest possible chord, so it
 * means "how much of a full-width line's worth of ink supports this". That is a
 * real quantity from the detector's own evidence rather than a constant dressed
 * up as one.
 */
const ANGLE_STEP_DEGREES = 0.5;

/**
 * The shortest mark that may be **considered as one side of a rectangle**, in
 * points — and the lower of this detector's two length bars.
 *
 * A side of a rectangle and a rule standing on its own are not the same claim,
 * and the corpus leaves no room to pretend otherwise. Lower the floor far enough
 * to see the title block's 60.5 pt short edge and the five open zig-zag
 * polylines on every sheet arrive as segments of **40.9 to 58.3 pt** — 28 of
 * them on `RF09.300.clean` alone, every one a false positive against a `LINE`
 * truth that records them as polylines.
 *
 *     longest zig-zag segment    58.3 pt
 *     shortest title block edge  60.5 pt
 *
 * **Two point two points apart.** No single length threshold separates those,
 * and a constant chosen in that gap would be fitted to this corpus rather than
 * measured on it — it would not survive one drawing whose zig-zag is a
 * millimetre longer.
 *
 * What separates them is not length, it is corroboration. Three further segments
 * meeting a fourth at square corners, with the corners themselves evidenced, is
 * a reason to believe all four are drawing; a short segment agreeing with
 * nothing is not. So this bar admits a segment to the *assembly*, and
 * `MIN_STANDALONE_RULE_POINTS` decides whether what survives assembly is
 * reported on its own.
 *
 * This is the shape `circles.ts` already uses: two criteria that each look
 * sufficient, where only the conjunction is clean.
 *
 * **The sweep**, all 24 images through the acceptance runner's own scorer:
 *
 *     sideLen (pt)   20    30    35    40    45    50    55    58
 *     LINE  tp/fp/fn  213/9/3 at every one of them
 *     LWPOLYLINE      48/0/3 -- 48/0/0 at every one of them
 *     lines traced   814   739   730   709   707   707   466   382
 *
 * Flat over the whole range, so the corpus does not choose this by score. Forty
 * is taken because it is the only quantity here with a binding case: the title
 * block's short edge traces at 60.5 pt on a clean sheet, and side coverage runs
 * as low as 0.707 on a degraded one, so the bar has to sit near 42 pt to hold it
 * on the worst variant. Forty is 1.5x below the shortest side the corpus
 * contains and 1.3x below the worst traced length of it. 58 would score the same
 * here and sit 2.2 pt from a zig-zag segment, which is a knife edge rather than a
 * margin.
 */
const MIN_SIDE_LENGTH_POINTS = 40;

/**
 * The shortest mark worth reporting as a rule in its own right, **in points**.
 *
 * This was two constants and neither was a quantity about the thing it measured:
 * `MIN_SEGMENT_PIXELS = 10` on the traced run, and a vote floor of 0.3 of the
 * image's shorter side. The first is a claim that the shortest interesting mark
 * scales with the scanner; the second that it scales with the paper. Both are
 * wrong in the way `HANDOFF.md` and `TRACE_GAP_POINTS` already record, and the
 * corpus prices the second exactly:
 *
 *     page (pt)             shorter   floor at 0.3   170 pt block   60 pt edge
 *     A4  595.4 x  841.9     595.4      178.6 pt        28.55%        10.08%
 *     A3  802.1 x 1160.6     802.1      240.6 pt        21.19%         7.48%
 *
 * The same 170 pt title block is 28.55% of the sheet on A4 and 21.19% on A3, so
 * the floor asks A3 for a 240.6 pt rule and A4 for a 178.6 pt one — 35% more,
 * for a quantity that has nothing to do with paper size. That is the second cost
 * of the vote floor, recorded as L1, and it is why the title block is invisible
 * at every resolution rather than at some of them.
 *
 * **The two populations this sits between, measured over all 24 truth files:**
 *
 *     true LINEs            216   min  475.3 pt   median 475.3   max  721.9
 *     zig-zag segments      360   every one of them exactly 50.6 pt
 *
 * A factor of **9.4** separates them, and nothing lives in between. 150 pt sits
 * 2.96x above the zig-zags and 3.17x below the shortest true rule — as near the
 * geometric middle as a round number gets, which is how a threshold between two
 * populations is chosen on a ratio scale. Contrast the 2.2 pt that separates a
 * zig-zag segment from a title block edge, where no threshold exists at all;
 * that gap is why there are two constants and not one.
 *
 * **The sweep**, all 24 images through the acceptance runner's own scorer:
 *
 *     standalone (pt)   60    80   100   150   200   300   400   470
 *     LINE  tp          213   213   213   213   213   213   213   213
 *     LINE  fp           21     9     9     9     3     3     3     3
 *     LINE  fn            3     3     3     3     3     3     3     3
 *
 * Below 80 the zig-zag segments start arriving as rules. Recall never moves,
 * because every true rule is 475.3 pt or longer and no value tested comes near
 * it.
 *
 * **And the step at 200 pt is a trap this constant is deliberately not tuned
 * into.** The six false positives it removes are the freeform Bezier on the six
 * RF01 variants — ADR 0010's open item — which the detector reports a 181.0 pt
 * line through because it is real ink the contract has no entity for. Raising
 * the bar to 200 pt would delete that report without the detector understanding
 * the mark any better, and this repository's first promise is that nothing is
 * silently lost. Precision bought by declining to report ink is that promise
 * inverted.
 *
 * It is the same trap the ink ledger's band sweep records from the other side:
 * false positives fall monotonically all the way to 470 pt, so a rule that
 * minimises them can always be satisfied by raising the bar, and a figure that
 * only ever improves cannot choose anything. 150 pt is chosen on the two
 * populations instead, and the Bezier stays on the record as a false positive.
 */
const MIN_STANDALONE_RULE_POINTS = 150;

/**
 * The floor under that conversion, in pixels, and it is the old constant.
 *
 * Same argument as `MIN_TRACE_GAP_PIXELS`: `houghLines` is exported and exercised
 * on synthetic images with no physical scale, where `pixelsPerPoint` defaults to
 * 1 and a point is a pixel. Ten is what those images were getting before this
 * change, the corpus has nothing to say about an image that declares no scale,
 * and moving them would be an unmeasured change dressed up as a fix.
 */
const MIN_RULE_LENGTH_PIXELS = 10;

/**
 * How much of that length the accumulator must see before a peak is traced.
 *
 * The vote floor and the length gate measure the same quantity by two different
 * means, and the accumulator's is the worse one: a rule sitting between angle
 * buckets spreads its votes over `length x sin(quarter step)` cells of r, so its
 * peak under-counts its length. `HANDOFF.md` records the arithmetic — 13 cells at
 * 300 DPI on a 700 pt rule and 26 at 600.
 *
 * So the vote floor is set BELOW the length it stands in for, and the traced run
 * is what actually decides. Votes propose; the ink disposes. A floor equal to the
 * length gate would silently reject rules the length gate would have kept, which
 * is the failure mode this whole change exists to remove.
 *
 * **The corpus cannot choose this constant, and says so clearly.** Swept over all
 * 24 images at 0.3, 0.5, 0.7, 0.8, 0.9, 1.0 and 1.2, every score is identical —
 * LINE 213/9/3, CIRCLE 96/0/0, LWPOLYLINE 48/0/0 — and so is the number of lines
 * that survive the trace. The floor is simply not binding on these drawings: the
 * length gate rejects everything it would have. A half is kept for the reason
 * above rather than for a measured one, and that is stated rather than dressed up
 * as a sweep result. What the sweep does establish is that no value in that range
 * costs anything here.
 */
const VOTE_FLOOR_FRACTION = 0.5;

/**
 * The floor under the vote floor, for the same unscaled images.
 *
 * Twenty, unchanged: it is what `Math.max(20, ...)` supplied before.
 */
const MIN_VOTE_FLOOR = 20;

/**
 * How far off a line the ink may sit and still count as on it.
 *
 * One pixel, and it stays one pixel because `traceSegment` refits the line to the
 * ink instead of widening the window to chase the accumulator's quantisation.
 */
const TRACE_SEARCH_RADIUS = 1;

/**
 * How much blank paper ends a traced run, **in points**.
 *
 * This was four PIXELS, and the docstring on it argued that four sits between the
 * two gap populations "at every DPI in the corpus, since the paper gap scales
 * with the image and the speckle does not". **Both halves of that sentence are
 * wrong, and the corpus says so.**
 *
 * The paper gap does not scale with the image, it scales with the SHEET: an
 * interior rule stops 19.68 pt short of the frame on every RF05 sheet, which is
 * 82 pixels at 300 DPI and 164 at 600. And there is no speckle inside the
 * strokes to survive. Walking every truth LINE of all 24 images with the trace's
 * own +/-1 px window (`scripts/tmp-gapsweep.ts`), the widest gap inside a stroke
 * measures **0.00 pt — on every image, at every DPI, clean and noisy.** The
 * corpus contains no ink dropouts at all. What the four-pixel gap was tripping
 * over was not paper, it was the trace's own aim; see `traceSegment`.
 *
 * So both quantities are physical and neither is a number of pixels. Four pixels
 * is 0.96 pt at 300 DPI, which is why the constant worked there, and 0.48 pt at
 * 600, which is why it did not: at 600 the same sheet got half the tolerance.
 *
 * **The sweep, on the converged trace, scored on all 24 images by the acceptance
 * runner's own scorer** (LINE tp/fp/fn, 216 truth lines):
 *
 *     gap (pt)   0.1    0.2    0.25   0.5    1.0    1.5    1.75   2.0    2.5    3.0
 *     tp         209    211    216    216    216    216    215    211    207    204
 *     fp         110    108    103    103    103    103    104    108    112    115
 *     fn           7      5      0      0      0      0      1      5      9     12
 *
 * The plateau is 0.25 pt to 1.5 pt, six-fold wide and perfectly flat. Below it
 * runs break inside real rules (RF05.450.noisy drops to 5/8/4 at 0.1 pt); above
 * it they annex what lies past the rule's end (RF01.300.noisy, RF02.300.noisy,
 * RF02.450.noisy, RF05.450.noisy and RF09.450.noisy each lose one at 2 pt).
 * One point is the middle of the separation between the widest gap that must be
 * bridged (0.2 pt) and the narrowest that must break (1.75 pt): **0.8 pt of
 * margin below and 0.75 pt above**, or five-fold and 1.75-fold.
 *
 * **The alternative, and why it was rejected.** The gap can also be a fraction of
 * the image's shorter side, which needs no context at all. Swept over the same
 * corpus it is indistinguishable — 0.0005, 0.001, 0.0012 and 0.002 all give
 * 216/103/0 — so the accuracy figure cannot choose between them, and the reason
 * to prefer points is what the fraction actually measures. At 0.002 it is 1.20 pt
 * on A4 and 1.68 pt on A3 at the same DPI: 40% more tolerance because the paper
 * is bigger, for a quantity that has nothing to do with paper size. Crop the scan
 * to half a sheet and it halves again. `pixelsPerPoint` is the real conversion,
 * `DetectorContext` already carries it, and a detector that ignores it is
 * choosing to be resolution-dependent.
 */
const TRACE_GAP_POINTS = 1;

/**
 * The floor under that conversion, in pixels — and it is the old constant.
 *
 * `houghLines` is exported and exercised on synthetic images that have no
 * physical scale, where `pixelsPerPoint` defaults to 1 and one point is one
 * pixel. `inkAlong` emits at most one point per step, so a gap of one or two
 * pixels ends a run at the first missing pixel or two: the constant would have
 * stopped expressing a distance on paper and started expressing the raster's own
 * resolution. Four is what those images were getting before this change, the
 * corpus has nothing to say about an image with no declared scale, and moving
 * them would be an unmeasured change dressed up as a fix.
 *
 * It costs nothing at any resolution the corpus contains, because at the
 * coarsest of them the two rules agree: 4.167 px/pt makes one point 4.17 px,
 * which rounds to the four the floor would have supplied anyway. **300 DPI is
 * therefore bit-for-bit unchanged by this commit**; only 450 and 600 move, which
 * is exactly where the defect was.
 */
const MIN_TRACE_GAP_PIXELS = 4;

/**
 * How far apart two traced segments may lie and still be one rule, **in points**.
 *
 * A lower vote floor exposes a duplication this detector has always had and the
 * old floor hid. On `RF01.300.clean` at a floor of 10% of the shorter side the
 * frame's bottom edge comes back FIVE times and its right edge twice, every copy
 * reporting the same extent to a decimal.
 *
 * **It is not an accumulator defect and `isPeak` cannot fix it.** The peaks that
 * produce those copies are genuinely distinct and lie far enough apart in
 * (angle, r) that no plausible suppression window joins them. What makes their
 * answers identical is `traceSegment` refitting to the ink — the same mechanism
 * that makes the trace correct in the first place. Several different seeds walk
 * onto the same stroke and converge on it.
 *
 * So the collapse happens after the trace, where the duplication is created.
 *
 * In POINTS, because what it measures is a distance on the paper: two marks
 * closer together than a drawn stroke is wide are one mark. The two populations
 * are far apart — a duplicate sits within a stroke width of its twin, while the
 * corpus's interior rules are 30 pt apart and its closest distinct parallel pair
 * is the title block's edge against the frame's at 60 pt — so this constant sits
 * in a wide empty gap rather than between two crowded ones.
 */
const DEDUP_OFFSET_POINTS = 2;

/**
 * How far from parallel two traced segments may be and still be one rule.
 *
 * Generous on purpose, and it costs nothing: the offset test above is what
 * separates two rules, and two segments that agree in offset to within a stroke
 * width AND overlap along their shared direction are the same mark whatever
 * their fitted angles say.
 *
 * **A degree was too tight, and a short edge is why.** The title block's 60.5 pt
 * left edge on `RF09.300.clean` is traced twice, and the two copies fit angles
 * 90.45 and 89.32 degrees — **1.13 degrees apart**, on segments whose endpoints
 * agree to 3.0 px. A refit converges tightly on a long rule and loosely on a
 * short one, because it has less ink to fit.
 *
 * Five degrees cannot merge anything the offset test would keep apart: over a
 * 60 pt segment five degrees is 5.2 pt end to end, which is already past the
 * 2 pt offset both endpoints have to satisfy. On anything longer the offset test
 * binds harder still. It is an early-out, not the discriminator.
 */
const DEDUP_ANGLE_DEGREES = 5;

/**
 * When a refit has stopped moving: half a pixel of drift end to end.
 *
 * Expressed against the image diagonal, so the test is "could another pass move a
 * traced endpoint by half a pixel" rather than a fixed number of degrees, which
 * would mean something different on a 2481 px sheet and a 9718 px one.
 */
const REFIT_SETTLED_PIXELS = 0.5;

/**
 * How many refits are allowed before the trace gives up and uses what it has.
 *
 * Measured, not guessed: over the 319 Hough peaks of the 24-image corpus the
 * convergence test above stopped at a median of 1 pass, 3 at the 90th percentile
 * and 10 at the worst. Twelve leaves headroom over the worst case and bounds the
 * cost of a peak that oscillates instead of settling — each pass is one walk of
 * the diagonal, against the accumulator's own pass over every ink pixel times 360
 * angles, so a dozen of them is not measurable in the runtime.
 */
const MAX_REFIT_PASSES = 12;

export function houghLines(
  image: BinaryImage,
  {
    minVotes,
    angleStepDegrees = ANGLE_STEP_DEGREES,
    pixelsPerPoint = 1,
    // Exposed for one reason: a test that pins it to 1 reproduces the single-pass
    // trace this replaced, so "the refit fixes the truncation" has a negative
    // control rather than only a passing assertion. Nothing in the product sets it.
    maxRefitPasses = MAX_REFIT_PASSES,
    // The two length bars, exposed for the same reason and used the same way: the
    // sweep in ADR 0011 varies them over the whole corpus through the acceptance
    // runner's own scorer. Nothing in the product sets either.
    minSideLengthPoints = MIN_SIDE_LENGTH_POINTS,
    voteFloorFraction = VOTE_FLOOR_FRACTION,
  }: {
    minVotes?: number;
    angleStepDegrees?: number;
    pixelsPerPoint?: number;
    maxRefitPasses?: number;
    minSideLengthPoints?: number;
    voteFloorFraction?: number;
  } = {},
): DetectedLine[] {
  const { width, height, ink } = image;
  const diagonal = Math.ceil(Math.hypot(width, height));
  const angles = Math.round(180 / angleStepDegrees);
  const stride = 2 * diagonal + 1;
  const votes = new Int32Array(angles * stride);

  const cosines = new Float64Array(angles);
  const sines = new Float64Array(angles);
  for (let a = 0; a < angles; a += 1) {
    const radians = (a * angleStepDegrees * Math.PI) / 180;
    cosines[a] = Math.cos(radians);
    sines[a] = Math.sin(radians);
  }

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (ink[y * width + x] === 0) continue;
      for (let a = 0; a < angles; a += 1) {
        const r = Math.round(x * cosines[a]! + y * sines[a]!) + diagonal;
        votes[a * stride + r] = votes[a * stride + r]! + 1;
      }
    }
  }

  // A rule worth reporting is a mark of some length on the paper. Both gates are
  // that one length: the accumulator's is the cheap proposal, the traced run is
  // the real test, and the proposal is deliberately the looser of the two.
  const minSegmentPixels = Math.max(MIN_RULE_LENGTH_PIXELS, minSideLengthPoints * pixelsPerPoint);
  const floor =
    minVotes ?? Math.max(MIN_VOTE_FLOOR, Math.round(minSegmentPixels * voteFloorFraction));

  // How far one line's votes spread in `r`, and therefore how far apart two cells
  // must be before the accumulator can call them different lines. See `isPeak`.
  const walkSteps = Math.ceil(Math.hypot(width, height));
  const walkA = makeWalk(walkSteps);
  const walkB = makeWalk(walkSteps);

  const radiusWindow = Math.max(
    1,
    Math.round(diagonal * Math.sin(((angleStepDegrees / 4) * Math.PI) / 180)),
  );
  const found: Traced[] = [];

  // The one place the physical scale enters. `pixelsPerPoint` defaults to 1, so an
  // image with no scale — every synthetic one in the tests — reads a point as a
  // pixel and the detector still answers in its own frame.
  const gapPixels = Math.max(
    MIN_TRACE_GAP_PIXELS,
    Math.round(TRACE_GAP_POINTS * pixelsPerPoint),
  );

  for (let a = 0; a < angles; a += 1) {
    for (let r = 0; r <= 2 * diagonal; r += 1) {
      const count = votes[a * stride + r]!;
      if (count < floor) continue;
      // Keep only local maxima, or one rule reports as a dozen near-identical ones.
      if (!isPeak(votes, angles, diagonal, a, r, radiusWindow)) continue;
      const segment = traceSegment(
        image,
        a * angleStepDegrees,
        r - diagonal,
        gapPixels,
        maxRefitPasses,
        minSegmentPixels,
        walkA,
        walkB,
      );
      if (segment === null) continue;
      found.push({ ...segment, confidence: Math.min(1, count / diagonal), votes: count });
    }
  }

  const kept = collapseDuplicates(found, DEDUP_OFFSET_POINTS * pixelsPerPoint, DEDUP_ANGLE_DEGREES);

  // Sorted so the output order is a property of the image, not of the scan order.
  return kept
    .map(({ votes: _votes, ...line }) => line)
    .sort((p, q) => p.y1 - q.y1 || p.x1 - q.x1 || p.y2 - q.y2 || p.x2 - q.x2);
}

/** A traced segment with the accumulator evidence that produced it. */
interface Traced extends DetectedLine {
  votes: number;
}

/**
 * Several traces of one mark, reduced to the best-evidenced one.
 *
 * Two segments are the same mark when all three hold: they run in the same
 * direction, they lie within `offsetPixels` of each other perpendicular to it,
 * and they **overlap** along it. The overlap test is not decoration — without it
 * this would also merge two collinear rules with a gap between them, which is a
 * different drawing, and one the trace has already declined to bridge.
 *
 * The survivor is the one the accumulator evidenced most. Ties break on geometry
 * rather than on discovery order, so two images that differ only in scan order
 * cannot produce different answers: the acceptance runner's determinism check
 * rests on that.
 */
function collapseDuplicates(
  traced: readonly Traced[],
  offsetPixels: number,
  angleDegrees: number,
): Traced[] {
  const ordered = [...traced].sort(
    (p, q) => q.votes - p.votes || p.y1 - q.y1 || p.x1 - q.x1 || p.y2 - q.y2 || p.x2 - q.x2,
  );

  const kept: Traced[] = [];
  for (const candidate of ordered) {
    if (!kept.some((survivor) => sameMark(survivor, candidate, offsetPixels, angleDegrees))) {
      kept.push(candidate);
    }
  }
  return kept;
}

function sameMark(
  a: Traced,
  b: Traced,
  offsetPixels: number,
  angleDegrees: number,
): boolean {
  const aAngle = segmentDirection(a);
  if (angleBetween(aAngle, segmentDirection(b)) > angleDegrees) return false;

  const radians = (aAngle * Math.PI) / 180;
  const alongX = Math.cos(radians);
  const alongY = Math.sin(radians);
  // The normal to `a`, which is what a perpendicular offset is measured along.
  const normalX = -alongY;
  const normalY = alongX;

  const aOffset = a.x1 * normalX + a.y1 * normalY;
  for (const [x, y] of [
    [b.x1, b.y1],
    [b.x2, b.y2],
  ]) {
    if (Math.abs(x! * normalX + y! * normalY - aOffset) > offsetPixels) return false;
  }

  const span = (line: DetectedLine): [number, number] => {
    const first = line.x1 * alongX + line.y1 * alongY;
    const second = line.x2 * alongX + line.y2 * alongY;
    return [Math.min(first, second), Math.max(first, second)];
  };
  const [aLow, aHigh] = span(a);
  const [bLow, bHigh] = span(b);
  return Math.min(aHigh, bHigh) >= Math.max(aLow, bLow);
}

/** A segment's direction in degrees, folded into [0, 180): a line has no arrow. */
function segmentDirection(line: DetectedLine): number {
  const degrees = (Math.atan2(line.y2 - line.y1, line.x2 - line.x1) * 180) / Math.PI;
  return ((degrees % 180) + 180) % 180;
}

/** The smaller of the two angles between two directions, in [0, 90]. */
function angleBetween(a: number, b: number): number {
  const gap = Math.abs(a - b) % 180;
  return Math.min(gap, 180 - gap);
}

/**
 * Whether this accumulator cell dominates its neighbourhood.
 *
 * Ties are broken by scan order — an equal neighbour that comes first wins — so a
 * plateau of identical vote counts yields one detection rather than one per cell.
 * Without that, a perfectly straight rule whose peak spreads over two buckets is
 * reported twice, and the second copy is a false positive by construction.
 *
 * **The window in `r` is three cells wide no longer, and the accumulator's own
 * geometry says how wide it should be.** `HANDOFF.md` records the arithmetic: a
 * rule sitting between angle buckets spreads its votes over
 * `length x sin(quarter step)` cells of `r` — 13 cells at 300 DPI on a 700 pt
 * rule and 26 at 600. A three-cell window cannot suppress a ridge 13 cells wide,
 * so ONE rule was surviving as many peaks, and every one of them was traced
 * before the collapse downstream could notice they were the same mark.
 *
 * The cost of that was not the false positives — those were already collapsed —
 * it was the tracing. Between 4,677 and 10,663 peaks per corpus image each paid
 * a `traceSegment`, which walks the diagonal up to `MAX_REFIT_PASSES` times and
 * allocates a point per step. That is where 3.7 GB of peak RSS and four images
 * of 380 to 906 seconds came from.
 *
 * So the window is derived rather than chosen: two cells closer together in `r`
 * than one line's votes spread are not two lines the accumulator can tell apart.
 * `diagonal x sin(quarter step)` is that spread at its widest.
 *
 * **It lands on a physical distance, and the same one twice.** The diagonal and
 * the spread scale together, so the window is 9 px at 300 DPI and 19 px at 600 —
 * **2.2 pt at both**, which is `DEDUP_OFFSET_POINTS` to within a rounding. Two
 * marks closer than a stroke width are one mark, and the accumulator and the
 * collapse now say so in the same units for the same reason.
 */
function isPeak(
  votes: Int32Array,
  angles: number,
  diagonal: number,
  a: number,
  r: number,
  radiusWindow: number,
): boolean {
  const stride = 2 * diagonal + 1;
  const here = votes[a * stride + r]!;
  const window = 3;
  for (let da = -window; da <= window; da += 1) {
    const aa = a + da;
    if (aa < 0 || aa >= angles) continue;
    for (let dr = -radiusWindow; dr <= radiusWindow; dr += 1) {
      const rr = r + dr;
      if (rr < 0 || rr > 2 * diagonal) continue;
      if (da === 0 && dr === 0) continue;
      const there = votes[aa * stride + rr]!;
      if (there > here) return false;
      if (there === here && (aa < a || (aa === a && rr < r))) return false;
    }
  }
  return true;
}

/**
 * The extent of the ink actually on the line, not the infinite line.
 *
 * A Hough peak names a line across the whole plane. Reporting that as a segment
 * would draw every rule from edge to edge of the sheet, which is a different
 * drawing. So the ink along the line is walked, grouped into runs, and the
 * **longest contiguous run** is returned.
 *
 * The longest run rather than first-ink-to-last-ink, and the difference was
 * measured. On RF09.300.clean, first-to-last found all nine of the drawing's
 * horizontal rules at the correct height — 169.4 pt against a truth of 170 — but
 * reported every one of them spanning x 39.6 to 555.4 where the truth is 60 to
 * 535.276. The rules abut the frame, so the trace crossed 20 points of blank
 * paper, hit the frame's vertical edge, and annexed it. Twenty points of endpoint
 * error against a 0.92 pt matching tolerance turns nine correct detections into
 * nine misses, and it is the same defect as reporting the infinite line, just
 * smaller. A gap of `gapPixels` breaks a run, which the 82 px of paper between
 * rule and frame at 300 DPI comfortably does while a dropout in a stroke does not.
 *
 * **The refit is iterated, and that is the other half of the resolution defect.**
 * One refit was not enough on a long rule. The seed comes from the accumulator's
 * quantised line, up to a quarter of a 0.5 degree step off the ink, and a +/-1 px
 * window on a line 0.25 degrees adrift only holds the stroke for 2/tan(0.25 deg)
 * = 458 px. On A3 at 600 DPI a rule is 6016 px long, so the seed samples 8% of it
 * and the fit that comes out of that sample is still off. Instrumenting the trace
 * on RF05.600.noisy (`scripts/tmp-gaptrace.ts`) measured what that costs: the
 * single-refit walk met gaps of 8.4 to 37.1 pt INSIDE the nine rules, against the
 * 19.7 pt of paper between rule and frame that has to break a run. The two
 * populations overlapped, so no gap in any unit could have separated them — the
 * gaps were the trace losing the stroke, not the sheet losing ink, and along the
 * truth lines themselves the widest gap inside a stroke is 0.00 pt.
 *
 * Walking again from each new fit closes it, because every pass holds more of the
 * stroke than the last and so fits it better. Over the corpus's 319 peaks the
 * median peak needs 1 pass and the worst needs 10. On the whole corpus, at a 1 pt
 * gap, one refit scores LINE tp/fp/fn 200/119/16 and refitting to convergence
 * scores 216/103/0 — every truth line on every image, false negatives gone.
 */
function traceSegment(
  image: BinaryImage,
  angleDegrees: number,
  r: number,
  gapPixels: number,
  maxRefitPasses: number,
  minSegmentPixels: number,
  walk: Walk,
  scratch: Walk,
): { kind: 'line'; x1: number; y1: number; x2: number; y2: number } | null {
  // Pass one, on the accumulator's own quantised line. Whatever it finds is a
  // clean sample of the ink that voted for this bucket.
  inkAlong(image, angleDegrees, r, walk);
  if (walk.count < 2) return null;

  // Refit to that ink, which REMOVES the quantisation error rather than widening
  // the window to tolerate it. Widening was measured and rejected: a window big
  // enough to follow the drift at 600 DPI annexed neighbouring ink at 300, taking
  // that row from F1 0.818 to 0.636 while 600 rose from 0.091 to 0.727. A refit
  // improves both, because the second pass runs along the line the ink is actually
  // on and can stay one pixel wide.
  const settled = (REFIT_SETTLED_PIXELS / Math.hypot(image.width, image.height)) * (180 / Math.PI);
  let fitted = fitLine(walk);
  let points = walk;
  let spare = scratch;
  let count = 0;
  for (let pass = 1; pass <= maxRefitPasses; pass += 1) {
    inkAlong(image, fitted.angleDegrees, fitted.r, spare);
    if (spare.count < 2) break;
    // Swap rather than copy: the walk just taken becomes the current one and the
    // one it replaces becomes the scratch for the next pass. A pass that finds
    // too little ink breaks above without disturbing what is already held, which
    // is why there are two buffers and not one.
    const adopted = spare;
    spare = points;
    points = adopted;
    count = points.count;
    const next = fitLine(points);
    // Modulo 180, because `fitLine` names the line by its normal and a vertical
    // rule's normal sits on the 0/180 seam: a fit that crosses it flips the angle
    // by 180 and negates r, which is the SAME line and must count as no movement.
    const turned = Math.abs(next.angleDegrees - fitted.angleDegrees) % 180;
    fitted = next;
    if (Math.min(turned, 180 - turned) < settled) break;
  }
  if (count < 2) return null;

  // Split into runs at every gap, then take the longest. Walked as index ranges
  // over the buffer rather than as arrays of arrays: this runs once per Hough
  // peak, and there are thousands of those on a corpus sheet.
  const { t, x, y } = points;
  let bestStart = -1;
  let bestEnd = -1;
  let bestLength = -1;
  let runStart = 0;
  for (let i = 1; i <= count; i += 1) {
    if (i < count && t[i]! - t[i - 1]! <= gapPixels) continue;
    const end = i - 1;
    if (end > runStart) {
      const length = Math.hypot(x[end]! - x[runStart]!, y[end]! - y[runStart]!);
      if (length > bestLength) {
        bestLength = length;
        bestStart = runStart;
        bestEnd = end;
      }
    }
    runStart = i;
  }

  if (bestStart < 0 || bestLength < minSegmentPixels) return null;
  return { kind: 'line', x1: x[bestStart]!, y1: y[bestStart]!, x2: x[bestEnd]!, y2: y[bestEnd]! };
}

/**
 * Reusable scratch for one walk along a line: the ink found, and how much.
 *
 * This was an array of `{t, x, y}` objects built fresh on every pass, and
 * `inkAlong` also built a two-element `offsets` array on every step of every
 * walk. A walk is `2 x diagonal` steps, a trace runs up to `MAX_REFIT_PASSES` of
 * them, and a corpus sheet traces thousands of peaks — which came to hundreds of
 * millions of short-lived objects per image, and to a peak RSS the report
 * publishes.
 *
 * Two of these are allocated per `houghLines` call and reused by every trace.
 * The arrays are sized for the longest possible walk, so nothing grows.
 */
interface Walk {
  readonly t: Int32Array;
  readonly x: Int32Array;
  readonly y: Int32Array;
  count: number;
}

function makeWalk(steps: number): Walk {
  const size = 2 * steps + 1;
  return { t: new Int32Array(size), x: new Int32Array(size), y: new Int32Array(size), count: 0 };
}

/**
 * The ink lying on a line, walked end to end.
 *
 * One pixel either side of the line and no more, so that nothing belonging to a
 * neighbouring stroke is picked up. The search runs outward from the line itself,
 * so the pixel taken is the nearest to it rather than whichever edge of the
 * window happened to be scanned first.
 *
 * Writes into `out` and returns nothing: see `Walk` for why.
 */
function inkAlong(image: BinaryImage, angleDegrees: number, r: number, out: Walk): void {
  const { width, height, ink } = image;
  const radians = (angleDegrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const steps = (out.t.length - 1) / 2;
  let found = 0;

  for (let t = -steps; t <= steps; t += 1) {
    for (let step = 0; step <= TRACE_SEARCH_RADIUS; step += 1) {
      // The offsets to try at this distance from the line: just the line itself
      // at step 0, then each side of it. Written out rather than built as an
      // array, because this is the innermost loop of the whole detector.
      const tries = step === 0 ? 1 : 2;
      let hit = false;
      for (let k = 0; k < tries; k += 1) {
        const d = step === 0 ? 0 : k === 0 ? -step : step;
        const px = Math.round((r + d) * cos - t * sin);
        const py = Math.round((r + d) * sin + t * cos);
        if (px < 0 || px >= width || py < 0 || py >= height) continue;
        if (ink[py * width + px] === 0) continue;
        out.t[found] = t;
        out.x[found] = px;
        out.y[found] = py;
        found += 1;
        hit = true;
        break;
      }
      if (hit) break;
    }
  }
  out.count = found;
}

/**
 * The line those points actually lie on, in the accumulator's own parametrisation.
 *
 * Total least squares — the principal axis of the points' covariance — rather than
 * a fit of y against x, because a vertical rule has no slope in those terms and
 * every drawing has vertical rules.
 */
function fitLine(walk: Walk): { angleDegrees: number; r: number } {
  const { x, y, count } = walk;
  let meanX = 0;
  let meanY = 0;
  for (let i = 0; i < count; i += 1) {
    meanX += x[i]!;
    meanY += y[i]!;
  }
  meanX /= count;
  meanY /= count;

  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (let i = 0; i < count; i += 1) {
    const dx = x[i]! - meanX;
    const dy = y[i]! - meanY;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }

  // Direction of greatest spread; its perpendicular is the line's normal, which
  // is what `r = x cos + y sin` is measured along.
  const direction = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  const normalX = -Math.sin(direction);
  const normalY = Math.cos(direction);
  return {
    angleDegrees: (Math.atan2(normalY, normalX) * 180) / Math.PI,
    r: meanX * normalX + meanY * normalY,
  };
}

/**
 * The detector that ships: rules, the frame they sit inside, and circles.
 *
 * Three stages, in this order and not another:
 *
 * 1. **Hough for lines.** Unchanged, and still the only thing that reads the
 *    accumulator.
 * 2. **Rectangles out of those lines.** Four traced segments that close on each
 *    other with square corners are one closed `LWPOLYLINE`, not four `LINE`s —
 *    which is what the drawing says they are. This runs on the Hough output
 *    rather than on the image because a rectangle is a fact about the *lines*,
 *    and re-deriving it from ink would be a second detector disagreeing with the
 *    first.
 * 3. **Circles from the ink directly.** Connected components, not the
 *    accumulator: see `circles.ts` for why, and for the measurement that a Hough
 *    circle transform buys recall this already has at a precision cost it does
 *    not.
 *
 * **The order of 1 and 2 matters and the order of 3 does not.** Rectangle
 * assembly consumes lines, so it has to see them all; circle detection reads
 * pixels and shares nothing with either, which is why it is a separate module
 * with its own tests rather than another pass over the accumulator.
 *
 * **Between 2 and 3 sits the second length bar**, and it is here rather than in
 * `houghLines` because it is a question about what to report rather than about
 * what the ink supports. A segment that became a side of a rectangle has three
 * others vouching for it; one that survives assembly alone has nothing, so it is
 * held to `MIN_STANDALONE_RULE_POINTS` instead of `MIN_SIDE_LENGTH_POINTS`.
 * Without that the corpus's zig-zag polylines arrive as 28 false `LINE`s per
 * sheet, which is what the old single floor was accidentally suppressing along
 * with the title block.
 *
 * ADR 0009 named the two gaps this closes and proposed *masking* the frame. It
 * is not masked. A drawing frame is drawing content — it belongs in the DXF —
 * and this repository's first promise is that nothing is silently lost, so the
 * four edges are recognised as the rectangle they form rather than deleted for
 * scoring against a truth that records them as one closed polyline. The scored
 * outcome is identical and the output keeps the frame.
 */
export const baselineDetector: Detector = {
  name: 'baseline-hough',
  detect(image: BinaryImage, context: DetectorContext): readonly Detection[] {
    // The context was ignored here until the trace gap became a physical
    // quantity. Now three stages read it, and every one of them reads the same
    // field: the answer is still produced in the image's own pixel frame, and
    // `toEntities` alone converts to points.
    const { pixelsPerPoint } = context;
    const traced = houghLines(image, { pixelsPerPoint });
    const { polylines, lines } = assembleRectangles(traced, { pixelsPerPoint });
    const standalone = Math.max(
      MIN_RULE_LENGTH_PIXELS,
      MIN_STANDALONE_RULE_POINTS * pixelsPerPoint,
    );
    const rules = lines.filter(
      (line) => Math.hypot(line.x2 - line.x1, line.y2 - line.y1) >= standalone,
    );
    const circles = detectCircles(image, { pixelsPerPoint });
    return [...rules, ...polylines, ...circles];
  },
};
